import * as React from "react";
import { useEffect, useState } from "react";
import { BottomSheet } from "../../../calendar/BottomSheet";
import { useWindowManagerOptional } from "../../../windowManager/WindowManagerContext";
import { PLUS_FAB_VAR } from "../../../windowManager/bottomBars";
import { alleDokumente } from "../../../windowManager/browserFenster/fensterRegister";
import { IcoCheck as IcoHaken, IcoNotiz, IcoDokument, IcoKalender, IcoMail, IcoPlus, IcoPosteingang, IcoUpload } from "../../v3/vg3Icons";
import { plusZielVorschlag } from "../todoOffen";
import { useSheetKlasse } from "./sheetKlasse";
import { AustauschInhalt } from "../AustauschKnopfV4";
import type { AustauschDto } from "../../../../services/vorgangAustauschService";
import { KommentarEingabe } from "../../../notizbloecke/KommentarEingabe";
import type { KommentarZusatz } from "../../../notizbloecke/kommentarInhalt";
import type { AppUser } from "../../../../services/userService";
import { fristPille } from "./fristPille";
import {
    useAktionsKnoepfe,
    type AktionsErgebnis,
    type AktionsKnoepfeOptionen,
    type AktionsSchluessel,
} from "../aktionsKnoepfe";

/**
 * DAS PLUS AUF DEM HANDY (Plan „Vorgangsdetail mobil", PR 3; Artifact,
 * Artboard 2). Ersetzt dort die Aktionsleiste in jeder Karte: EIN runder
 * Knopf über der unteren Leiste, dahinter ein Sheet mit Kacheln.
 *
 *  · Vorgang OHNE To-dos: ein Schritt — Kachel wählen, Ziel ist der Vorgang.
 *  · „+ Hinzufügen" am aufgeklappten To-do: dasselbe Sheet mit FESTEM Ziel.
 *  · Vorgang MIT To-dos: Schritt 2 „<Aktion> zu welchem To-do?" (PR 4) —
 *    Vorschlag aus `plusZielVorschlag`, ohne Vorschlag ist „Weiter" gesperrt;
 *    kommt nur ein To-do in Frage, entfällt die Frage (Entscheidung 1).
 *    Erledigte stehen abgesetzt unter „Erledigte zeigen" (Entscheidung 2).
 *    „Neues To-do anlegen …" legt an und lässt die Aktion dort weiterlaufen.
 *
 * Die Wege selbst kommen unverändert aus `useAktionsKnoepfe` (Mail-Fenster,
 * Postfach-Auswahl, Upload, Office, Termin-Fenster) — kein zweites Modell,
 * nur ein anderer Wirt mit dem Ziel zur Laufzeit (`fuehreAus`).
 */

export { SHEET_KLASSE, useSheetKlasse } from "./sheetKlasse";

/** Wohin etwas kommt: ein To-do oder (`todoId` null) der Vorgang. */
export type PlusZiel = {
    todoId: number | null;
    name: string;
    /** Die laufende Notiz des Vorgangs (nur Aktion „Notiz", Vorgang mit To-dos). */
    laufend?: boolean;
};

/** Die Kacheln des ersten Schritts. */
export type PlusAktion = "notiz" | "mail" | "termin" | "dokument" | "austausch" | "todo";

/** Wofür das Sheet offen ist. */
export type PlusAnfrage = {
    /** Gesetzt: kein Ziel-Schritt, alles landet dort. */
    festesZiel?: PlusZiel;
    /** Gesetzt: Kachel schon gewählt (Mail-Knopf im Kopf). */
    aktion?: PlusAktion;
};

/** Was der Ziel-Schritt über ein To-do wissen muss. */
export type PlusTodo = {
    id: number;
    titel: string;
    erledigt: boolean;
    frist?: string | null;
    twv?: string | null;
};

type Kachel = { aktion: PlusAktion; label: string; icon: React.ReactNode };

const KACHELN: Kachel[] = [
    { aktion: "notiz", label: "Notiz", icon: <IcoNotiz size={20} /> },
    { aktion: "mail", label: "Mail", icon: <IcoMail size={20} /> },
    { aktion: "termin", label: "Termin", icon: <IcoKalender size={20} /> },
    { aktion: "dokument", label: "Dokument / Foto", icon: <IcoUpload size={20} /> },
    { aktion: "austausch", label: "Austausch", icon: <span className="vg4m-kachel-a">A</span> },
    { aktion: "todo", label: "To-do", icon: <IcoHaken size={20} /> },
];

/** Die Unterwahl hinter Mail und Dokument (Entscheidung 5: Postfach und Office gehören ins Plus). */
type Unterwahl = { key: AktionsSchluessel; label: string; text: string; icon: React.ReactNode };

export function unterwahlFuer(aktion: PlusAktion, o: { officeVerfuegbar: boolean }): Unterwahl[] {
    if (aktion === "mail") {
        return [
            { key: "mail-neu", label: "Neue Mail", text: "Schreiben und senden", icon: <IcoMail size={20} /> },
            {
                key: "mail-zuordnen",
                label: "Aus dem Postfach",
                text: "Eine vorhandene Mail zuordnen",
                icon: <IcoPosteingang size={20} />,
            },
        ];
    }
    if (aktion === "dokument") {
        return [
            {
                key: "datei",
                label: "Foto oder Datei",
                text: "Kamera, Fotos oder Dateien des Geräts",
                icon: <IcoUpload size={20} />,
            },
            {
                key: "dokument-neu",
                label: "Aus KMVS",
                text: "Aus der Kundenakte oder einem anderen Vorgang",
                icon: <IcoDokument size={20} />,
            },
            ...(o.officeVerfuegbar
                ? [
                      {
                          key: "office-neu" as const,
                          label: "Neues Office-Dokument",
                          text: "Leeres Word oder Excel anlegen",
                          icon: <IcoDokument size={20} />,
                      },
                  ]
                : []),
        ];
    }
    return [];
}

/** Die Frage im Ziel-Schritt (Artifact: „<Aktion> zu welchem To-do?"). */
export function zielFrage(aktion: PlusAktion): string {
    if (aktion === "notiz") return "Notiz zu welchem To-do?";
    if (aktion === "mail") return "Mail zu welchem To-do?";
    if (aktion === "termin") return "Termin zu welchem To-do?";
    if (aktion === "dokument") return "Dokument zu welchem To-do?";
    if (aktion === "austausch") return "Austausch zu welchem To-do?";
    return "Zu welchem To-do?";
}

/** Der Hinweis nach dem Anlegen — „2 Dokumente hochgeladen – in ‚Gutachten‘". */
export function ergebnisText(e: AktionsErgebnis, zielName: string): string {
    const was =
        e.art === "mail"
            ? "Mail zugeordnet"
            : e.anzahl === 1
              ? "Dokument hochgeladen"
              : `${e.anzahl} Dokumente hochgeladen`;
    return `${was} – in „${zielName}“`;
}

/**
 * Das Anlegen eines To-dos im Sheet — Zustand und Kasten gehören der Seite
 * (`TodoAnlegenKasten`, ohne To-dos mit `erstesTodo`: der Vorgang wird
 * To-do 1). `starte` öffnet den Entwurf; nach dem Anlegen ruft die Seite
 * `nachAnlage` mit der neuen Id.
 */
export type PlusTodoAnlage = {
    starte: (nachAnlage: (neueId: number, titel: string) => void) => void;
    abbrechen: () => void;
    /** Der Kasten, solange ein Entwurf offen ist; sonst null. */
    kasten: React.ReactNode | null;
};

type Schritt = "aktion" | "ziel" | "unter" | "todo" | "notiz" | "austausch";

/** Was das Notiz-Sheet abschickt. */
export type PlusNotiz = { text: string; mentions: string[]; zusatz?: KommentarZusatz; ziel: PlusZiel };

export const PlusSheet: React.FC<{
    anfrage: PlusAnfrage | null;
    onSchliessen: () => void;
    /** Die Optionen des Vorgangs — OHNE `todoId`, das Ziel kommt zur Laufzeit. */
    optionen: AktionsKnoepfeOptionen;
    /** Ziel, wenn der Vorgang keine To-dos hat. */
    standardZiel: PlusZiel;
    /** Name eines Ziels für den Hinweis. */
    zielName: (todoId: number | null) => string;
    hinweis: (text: string) => void;
    /** Die To-dos in Anzeigereihenfolge; leer = Vorgang ohne To-dos (ein Schritt). */
    todos?: readonly PlusTodo[];
    /** Gerade aufgeklappte To-dos (Vorschlag „· gerade offen"). */
    offen?: ReadonlySet<number>;
    /** Zuletzt geöffnetes To-do dieses Vorgangs. */
    zuletzt?: number | null;
    todoAnlage?: PlusTodoAnlage;
    /**
     * Neue Notiz aus dem Notiz-Sheet (PR 5). Fehlt er, gibt es keine Kachel
     * „Notiz". Bestehende Notizen bearbeitet man direkt im Notizfeld.
     */
    notizAnlegen?: (n: PlusNotiz) => Promise<void>;
    /**
     * Mit To-dos steht im Ziel-Schritt der Notiz zusätzlich „Notizen zum
     * Vorgang" — die laufende Notiz (User: „Vorgang allgemein" nur für
     * Notizen). Für alle anderen Aktionen nie: dort gehört alles einem To-do.
     */
    laufendeNotiz?: boolean;
    users?: AppUser[];
    /**
     * Kachel „Austausch" (Entscheidung 9, PR 7): dasselbe Formular wie hinter
     * dem A, für das gewählte Ziel. Fehlt es, gibt es die Kachel nicht.
     */
    austausch?: { liste: readonly AustauschDto[]; canEdit: boolean; onGeaendert: () => void | Promise<void> };
}> = ({
    anfrage,
    onSchliessen,
    optionen,
    standardZiel,
    zielName,
    hinweis,
    todos = [],
    offen = KEINE,
    zuletzt = null,
    todoAnlage,
    notizAnlegen,
    laufendeNotiz = false,
    users = [],
    austausch,
}) => {
    const { fuehreAus, elemente, officeVerfuegbar } = useAktionsKnoepfe({
        ...optionen,
        onErgebnis: (e) => hinweis(ergebnisText(e, zielName(e.ziel.todoId ?? null))),
    });
    const [schritt, setSchritt] = useState<Schritt>("aktion");
    const [aktion, setAktion] = useState<PlusAktion | null>(null);
    /** Das gewählte Ziel (nach dem Ziel-Schritt oder fest). */
    const [ziel, setZiel] = useState<PlusZiel | null>(null);
    /** Die Auswahl im Ziel-Schritt: To-do-Id, „neu" oder noch nichts. */
    const [wahl, setWahl] = useState<number | "neu" | "laufend" | null>(null);
    const [notizLaeuft, setNotizLaeuft] = useState(false);
    const [erledigteAuf, setErledigteAuf] = useState(false);

    const hatTodos = todos.length > 0;

    /* Solange das Sheet steht, tritt der Melde-Knopf zurück (`tickets.css`)
       — sonst läge er über Kacheln und Schließen-Kreuz. */
    useSheetKlasse(anfrage != null);

    const ausfuehren = (key: AktionsSchluessel, z: PlusZiel) => {
        onSchliessen();
        fuehreAus(key, { todoId: z.todoId });
    };

    /** Mit Ziel weiter: Unterwahl oder gleich ausführen. */
    const mitZiel = (a: PlusAktion, z: PlusZiel) => {
        setZiel(z);
        if (a === "termin") return ausfuehren("termin-neu", z);
        setSchritt(a === "notiz" ? "notiz" : a === "austausch" ? "austausch" : "unter");
    };

    const neuesTodo = (a: PlusAktion | null) => {
        if (!todoAnlage) return;
        setSchritt("todo");
        todoAnlage.starte((neueId, titel) => {
            const z: PlusZiel = { todoId: neueId, name: titel };
            if (a == null || a === "todo") {
                onSchliessen();
                hinweis(`To-do angelegt – „${z.name}“`);
                return;
            }
            // VERKETTET (Plan §3b): Die gewählte Aktion läuft mit der neuen
            // To-do-Id weiter, statt den Nutzer zurückzuwerfen.
            mitZiel(a, z);
        });
    };

    /** Kachel gewählt — braucht es den Ziel-Schritt? */
    const waehle = (a: PlusAktion) => {
        setAktion(a);
        if (a === "todo") return neuesTodo("todo");
        if (anfrage?.festesZiel) return mitZiel(a, anfrage.festesZiel);
        if (!hatTodos) return mitZiel(a, standardZiel);
        const v = plusZielVorschlag({
            todos: todos.map((t) => ({ id: t.id, erledigt: t.erledigt })),
            offen,
            zuletzt,
            weiteresZiel: a === "notiz" && laufendeNotiz,
        });
        if (v.ueberspringen && v.todoId != null) {
            // Nur EIN To-do kommt in Frage (Entscheidung 1): nicht fragen.
            return mitZiel(a, { todoId: v.todoId, name: zielName(v.todoId) });
        }
        setWahl(v.todoId);
        setErledigteAuf(v.todoId != null && todos.some((t) => t.id === v.todoId && t.erledigt));
        setSchritt("ziel");
    };

    // Zustand bei jedem Öffnen frisch; eine vorgewählte Kachel (Mail im Kopf)
    // startet gleich beim Ziel.
    useEffect(() => {
        if (!anfrage) {
            setSchritt("aktion");
            setAktion(null);
            setZiel(null);
            setWahl(null);
            return;
        }
        if (anfrage.aktion) waehle(anfrage.aktion);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anfrage]);

    /*
     * TASTATUR (Plan mobil, PR 8): Ein schmales PC-Fenster bekommt dieses
     * Layout mit Maus und Tastatur (Entscheidung 7). Der Fokus wandert deshalb
     * bei jedem Schritt ins Sheet — sonst stünde er hinter dem Schleier, und
     * Tab liefe durch die verdeckte Seite. Escape schließt (`BottomSheet`).
     */
    useEffect(() => {
        if (!anfrage) return;
        const t = window.setTimeout(() => {
            const koerper = document.querySelector<HTMLElement>(".ct-sheet .ct-sheet-body");
            if (!koerper || koerper.contains(document.activeElement)) return;
            for (const wahl of ["textarea", "input", "[aria-checked='true']", "[role='radio']", ".vg4m-kachel", ".vg4m-wahl", "button"]) {
                const el = koerper.querySelector<HTMLElement>(wahl);
                if (el) {
                    el.focus();
                    return;
                }
            }
        }, 0);
        return () => window.clearTimeout(t);
    }, [anfrage, schritt]);
    // Nach dem Schließen dorthin zurück, wo der Fokus vorher stand (das Plus).
    const vorherRef = React.useRef<HTMLElement | null>(null);
    useEffect(() => {
        if (!anfrage) return;
        vorherRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        return () => {
            const el = vorherRef.current;
            if (el?.isConnected && !document.querySelector(".ct-sheet")) el.focus();
        };
    }, [anfrage != null]); // eslint-disable-line react-hooks/exhaustive-deps

    const schliessen = () => {
        if (schritt === "todo") todoAnlage?.abbrechen();
        onSchliessen();
    };

    const zurueck = () => {
        if (schritt === "todo") todoAnlage?.abbrechen();
        if ((schritt === "unter" || schritt === "notiz" || schritt === "austausch") && hatTodos && !anfrage?.festesZiel && aktion && ziel && !ueberspringbar()) {
            setSchritt("ziel");
            return;
        }
        setSchritt("aktion");
        setAktion(null);
    };
    const ueberspringbar = () =>
        plusZielVorschlag({
            todos: todos.map((t) => ({ id: t.id, erledigt: t.erledigt })),
            offen,
            zuletzt,
            weiteresZiel: aktion === "notiz" && laufendeNotiz,
        }).ueberspringen;

    const offeneTodos = todos.filter((t) => !t.erledigt);
    const erledigte = todos.filter((t) => t.erledigt);
    const zweiSchritte = hatTodos && !anfrage?.festesZiel;

    const titel =
        schritt === "ziel" && aktion
            ? zielFrage(aktion)
            : schritt === "todo"
              ? "Neues To-do"
              : schritt === "notiz"
                ? "Neue Notiz"
                : schritt === "austausch"
                  ? "Austausch"
              : schritt === "unter" && aktion === "mail"
                ? "Mail"
                : schritt === "unter"
                  ? "Dokument / Foto"
                  : "Hinzufügen";

    const zielZeile = (t: PlusTodo) => {
        const pille = fristPille({ frist: t.frist ?? null, twv: t.twv ?? null, erledigt: t.erledigt });
        const teile = [
            t.erledigt ? "erledigt" : pille?.text,
            offen.has(t.id) ? "gerade offen" : zuletzt === t.id ? "zuletzt geöffnet" : null,
        ].filter(Boolean);
        return (
            <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={wahl === t.id}
                className={`vg4m-ziel${wahl === t.id ? " gewaehlt" : ""}${t.erledigt ? " erledigt" : ""}`}
                onClick={() => setWahl(t.id)}
            >
                <span className="vg4m-ziel-punkt" aria-hidden />
                <span className="vg4m-wahl-text">
                    <span className="vg4m-wahl-t">{t.titel || `To-do ${t.id}`}</span>
                    {teile.length > 0 && <span className="vg4m-wahl-u">{teile.join(" · ")}</span>}
                </span>
            </button>
        );
    };

    return (
        <>
            <BottomSheet open={anfrage != null} title={titel} onClose={schliessen}>
                {schritt !== "aktion" && !(schritt === "ziel" && anfrage?.aktion) && (
                    <button type="button" className="vg4m-zurueck" onClick={zurueck}>
                        ← Zurück
                    </button>
                )}
                {zweiSchritte && (schritt === "aktion" || schritt === "ziel") && (
                    <div className="vg4m-plus-schritt">{schritt === "aktion" ? "Schritt 1 von 2" : "Schritt 2 von 2"}</div>
                )}

                {schritt === "aktion" && (
                    <>
                        {!zweiSchritte && (
                            <div className="vg4m-plus-ziel">
                                {(anfrage?.festesZiel ?? standardZiel).todoId != null ? "zu To-do" : "zum Vorgang"}{" "}
                                <strong>„{(anfrage?.festesZiel ?? standardZiel).name}“</strong>
                            </div>
                        )}
                        <div className="vg4m-kacheln">
                            {KACHELN.filter(
                                (k) =>
                                    (k.aktion !== "todo" || (todoAnlage && !anfrage?.festesZiel)) &&
                                    (k.aktion !== "notiz" || notizAnlegen) &&
                                    (k.aktion !== "austausch" || austausch),
                            ).map((k) => (
                                <button key={k.aktion} type="button" className="vg4m-kachel" onClick={() => waehle(k.aktion)}>
                                    <span className="vg4m-kachel-icon" aria-hidden>
                                        {k.icon}
                                    </span>
                                    {k.label}
                                </button>
                            ))}
                        </div>
                        {zweiSchritte && (
                            <p className="vg4m-plus-hilfe">
                                Danach wählst du, zu welchem To-do es gehört. „To-do“ legt direkt ein neues an.
                            </p>
                        )}
                    </>
                )}

                {schritt === "ziel" && aktion && (
                    <>
                        <div role="radiogroup" aria-label="Ziel" className="vg4m-ziele">
                            {offeneTodos.map(zielZeile)}
                            {erledigte.length > 0 && (
                                <button
                                    type="button"
                                    className="vg4m-erledigte-knopf"
                                    aria-expanded={erledigteAuf}
                                    onClick={() => setErledigteAuf((v) => !v)}
                                >
                                    {erledigteAuf ? "Erledigte ausblenden" : `Erledigte zeigen (${erledigte.length})`}
                                </button>
                            )}
                            {erledigteAuf && <div className="vg4m-ziele-erledigt">{erledigte.map(zielZeile)}</div>}
                            {aktion === "notiz" && laufendeNotiz && (
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={wahl === "laufend"}
                                    className={`vg4m-ziel${wahl === "laufend" ? " gewaehlt" : ""}`}
                                    onClick={() => setWahl("laufend")}
                                >
                                    <span className="vg4m-ziel-punkt" aria-hidden />
                                    <span className="vg4m-wahl-text">
                                        <span className="vg4m-wahl-t">Notizen zum Vorgang</span>
                                        <span className="vg4m-wahl-u">keinem To-do zugeordnet</span>
                                    </span>
                                </button>
                            )}
                            {todoAnlage && (
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={wahl === "neu"}
                                    className={`vg4m-ziel${wahl === "neu" ? " gewaehlt" : ""}`}
                                    onClick={() => setWahl("neu")}
                                >
                                    <span className="vg4m-ziel-punkt" aria-hidden />
                                    <span className="vg4m-wahl-text">
                                        <span className="vg4m-wahl-t">Neues To-do anlegen …</span>
                                        <span className="vg4m-wahl-u">und es gleich dort ablegen</span>
                                    </span>
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            className="vg4m-weiter"
                            disabled={wahl == null}
                            onClick={() => {
                                if (wahl === "neu") return neuesTodo(aktion);
                                if (wahl === "laufend") {
                                    return mitZiel(aktion, { todoId: null, name: "Notizen zum Vorgang", laufend: true });
                                }
                                if (wahl == null) return;
                                mitZiel(aktion, { todoId: wahl, name: zielName(wahl) });
                            }}
                        >
                            {wahl == null ? "Bitte ein To-do wählen" : aktion === "termin" && wahl !== "neu" ? "Termin anlegen" : "Weiter"}
                        </button>
                    </>
                )}

                {schritt === "unter" && aktion && ziel && (
                    <>
                        <div className="vg4m-plus-ziel">
                            {ziel.todoId != null ? "zu To-do" : "zum Vorgang"} <strong>„{ziel.name}“</strong>
                        </div>
                        <div className="vg4m-unterwahl">
                            {unterwahlFuer(aktion, { officeVerfuegbar }).map((u) => (
                                <button key={u.key} type="button" className="vg4m-wahl" onClick={() => ausfuehren(u.key, ziel)}>
                                    <span className="vg4m-kachel-icon" aria-hidden>
                                        {u.icon}
                                    </span>
                                    <span className="vg4m-wahl-text">
                                        <span className="vg4m-wahl-t">{u.label}</span>
                                        <span className="vg4m-wahl-u">{u.text}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {schritt === "todo" && <div className="vg4m-todo-anlage">{todoAnlage?.kasten}</div>}

                {schritt === "austausch" && ziel && austausch && (
                    <>
                        <div className="vg4m-plus-ziel">
                            {ziel.todoId != null ? "zu To-do" : "zum Vorgang"} <strong>„{ziel.name}“</strong>
                        </div>
                        <AustauschInhalt
                            vorgangId={optionen.vorgangId}
                            todoId={ziel.todoId}
                            austausch={austausch.liste}
                            users={users}
                            canEdit={austausch.canEdit}
                            onGeaendert={austausch.onGeaendert}
                            onFertig={onSchliessen}
                        />
                    </>
                )}

                {/* DAS NOTIZ-SHEET (Plan §3d, PR 5): ein schlichtes Feld mit
                    @-Erwähnung und Anhang (Foto) — die Abschnittslogik des
                    großen Notizfelds braucht eine neue Notiz nicht. */}
                {schritt === "notiz" && ziel && notizAnlegen && (
                    <>
                        <div className="vg4m-plus-ziel">
                            {ziel.laufend ? "in" : ziel.todoId != null ? "zu To-do" : "zum Vorgang"}{" "}
                            <strong>„{ziel.name}“</strong>
                        </div>
                        <div className="vg4m-notiz-eingabe">
                            <KommentarEingabe
                                users={users}
                                stil="faden"
                                ariaLabel="Neue Notiz"
                                placeholder="Notiz schreiben …"
                                sendenLabel={notizLaeuft ? "Speichert …" : "Notiz anlegen"}
                                autoFocus
                                mitAnhang
                                onSenden={async (text, mentions, zusatz) => {
                                    if (notizLaeuft) return;
                                    setNotizLaeuft(true);
                                    try {
                                        await notizAnlegen({ text, mentions, ...(zusatz ? { zusatz } : {}), ziel });
                                        onSchliessen();
                                        hinweis(`Notiz angelegt – in „${ziel.name}“`);
                                    } finally {
                                        setNotizLaeuft(false);
                                    }
                                }}
                                onAbbrechen={zurueck}
                            />
                        </div>
                    </>
                )}
            </BottomSheet>
            {/* Dialoge und der versteckte Dateidialog — AUSSERHALB des Sheets,
                sonst verschwänden sie mit ihm. */}
            {elemente}
        </>
    );
};

const KEINE: ReadonlySet<number> = new Set();

/** Ist gerade ein Eingabefeld im Fokus? Dann steht die Tastatur — das Plus weicht. */
function istEingabe(el: Element | null): boolean {
    if (!(el instanceof HTMLElement)) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName;
    if (tag === "TEXTAREA" || tag === "SELECT") return true;
    if (tag !== "INPUT") return false;
    const typ = (el as HTMLInputElement).type;
    return !["button", "checkbox", "radio", "submit", "file", "range", "color"].includes(typ);
}

/**
 * Der runde Knopf (Artifact: 56 px, rechts über der unteren Leiste).
 *
 * Er verschwindet, sobald ein Fenster im Vollbild steht (Mail, Termin — dort
 * fehlt auch die untere Leiste) und solange ein Eingabefeld den Fokus hat
 * (Bildschirmtastatur). Solange er steht, setzt er `--plus-fab-h`; der
 * Melde-Knopf rückt um so viel nach oben.
 */
export const PlusKnopf: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = "Hinzufügen" }) => {
    const wm = useWindowManagerOptional();
    const fensterOffen = !!wm?.windows.some((w) => w.state !== "minimized" && !w.browserFenster);
    const [tippt, setTippt] = useState(() => (typeof document !== "undefined" ? istEingabe(document.activeElement) : false));
    useEffect(() => {
        const pruefe = () => setTippt(istEingabe(document.activeElement));
        // Beim Verlassen steht der neue Fokus erst im nächsten Takt fest.
        const raus = () => window.setTimeout(pruefe, 0);
        alleDokumente.addEventListener("focusin", pruefe);
        alleDokumente.addEventListener("focusout", raus);
        return () => {
            alleDokumente.removeEventListener("focusin", pruefe);
            alleDokumente.removeEventListener("focusout", raus);
        };
    }, []);
    const sichtbar = !fensterOffen && !tippt;
    useEffect(() => {
        if (!sichtbar) return;
        const root = document.documentElement;
        root.style.setProperty(PLUS_FAB_VAR, "72px");
        return () => {
            root.style.removeProperty(PLUS_FAB_VAR);
        };
    }, [sichtbar]);
    if (!sichtbar) return null;
    return (
        <button type="button" className="vg4m-fab" aria-label={label} title={label} onClick={onClick}>
            <IcoPlus size={26} />
        </button>
    );
};

/** Der Hinweis nach dem Anlegen (Artifact: dunkle Leiste mit „OK"), über dem Plus. */
export const PlusHinweis: React.FC<{ text: string | null; onSchliessen: () => void }> = ({ text, onSchliessen }) => {
    useEffect(() => {
        if (!text) return;
        const t = window.setTimeout(onSchliessen, 6000);
        return () => window.clearTimeout(t);
    }, [text, onSchliessen]);
    if (!text) return null;
    return (
        <div className="vg4m-hinweis" role="status" aria-live="polite">
            <span>{text}</span>
            <button type="button" onClick={onSchliessen}>
                OK
            </button>
        </div>
    );
};
